import { isoDateTime, z } from "../zod.js";
import { Problem } from "../common/problem.js";

// Module 03 planning, PR G-1: the master-data contract (spec 03 §2.1 to §2.4,
// §2.22, §5; D-349 rulings 16 to 20, D-351). Quantities are decimal strings in
// the product's base unit, never JSON numbers, and no planning response carries
// money. Lists answer `{items, next_cursor}`.

const Uuid = z.guid();

/** An ISO calendar date. The regex keeps the OpenAPI pattern; `z.iso.date()` refuses 2026-02-30. */
export const PlanningDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .check(z.iso.date())
  .openapi({ format: "date", example: "2026-10-05" });

/** True when an ISO date falls on a Monday, the start of an ISO week bucket (spec 03 §2). */
export function isPlanningMonday(value: string): boolean {
  const day = new Date(`${value}T00:00:00Z`).getUTCDay();
  return day === 1;
}

/** A bucket bound: an ISO date that is a Monday. */
export const PlanningMonday = PlanningDate.superRefine((value, ctx) => {
  if (!isPlanningMonday(value)) {
    ctx.addIssue({
      code: "custom",
      message: "must be a Monday",
      params: { reason: "not_monday" },
    });
  }
}).openapi({ format: "date", example: "2026-10-05", description: "A Monday" });

/** A stored `numeric(14,3)` quantity as Postgres prints it. */
export const PlanningQty = z
  .string()
  .regex(/^\d{1,11}(\.\d{1,3})?$/)
  .openapi({ example: "6000.000", description: "Exact decimal, at most 3 places (numeric(14,3))" });

/** A written quantity that must be above zero. */
export const PlanningPositiveQty = PlanningQty.refine((v) => Number(v) > 0, {
  message: "must be above zero",
});

/** A stored `numeric(5,2)` percentage. */
export const PlanningPct = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/)
  .openapi({ example: "20.00", description: "Exact decimal, at most 2 places (numeric(5,2))" });

const Timestamp = isoDateTime();

/** The `updated_at` a write was based on; null for a row the caller read as unwritten. */
const ExpectedUpdatedAt = Timestamp.nullable().openapi({
  description:
    "The `updated_at` the caller last read, or null when it read no row. A stale value answers 409.",
});

const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(200).optional();

/** The facility every planning list and singleton read requires (spec 03 §5). */
export const PlanningFacilityQuery = z.object({ facility_id: Uuid });

// --- §2.22 planning settings --------------------------------------------------

/** The facility's planning settings. A facility whose row was never written reads the defaults with `updated_at` null. */
export const PlanningSettings = z
  .object({
    facility_id: Uuid,
    mps_auto_approve_delta_pct: PlanningPct,
    mps_facility_admin_delta_pct: PlanningPct,
    ctp_safety_margin_days: z.number().int().min(0),
    promise_ontime_downgrade_pct: PlanningPct,
    horizon_weeks: z.number().int().min(1).max(52),
    /** How long the demand debounce waits before its refresh runs (§2.23). */
    demand_refresh_debounce_minutes: z.number().int().min(1).max(60),
    /** The business date whose nightly refresh the worker last claimed; null means never. Read-only. */
    nightly_refreshed_on: PlanningDate.nullable(),
    /** The Monday whose weekly MPS proposal the worker last claimed; null means never. Read-only (G-3). */
    weekly_proposed_on: PlanningMonday.nullable(),
    /** The facility calendar's shift pattern (§2.22, P3-H-1); null when the facility has no calendar. */
    facility_shift_pattern_id: Uuid.nullable(),
    /** The business date whose nightly MRP pass claimed the facility; null means never. Read-only (P3-H-2). */
    nightly_mrp_on: PlanningDate.nullable(),
    /** Working days between a planned order's release and its start (§3.3, P3-H-2). */
    mrp_release_buffer_days: z.number().int().min(0).max(10),
    /** How far a firmed order's share may differ from its quantity before a quantity message (§3.3, P3-H-2). */
    mrp_qty_tolerance_pct: PlanningPct,
    /** How many days the finite schedule spans from the facility's today (§3.5, P3-I-2). */
    schedule_horizon_days: z.number().int().min(7).max(84),
    /** How much later a due date may be for the solver to pull an operation into a run (D-397 ruling 5). */
    schedule_batching_window_hours: z.number().int().min(1).max(336),
    /** How long the solve debounce waits before the solve runs (§2.19c). */
    schedule_solve_debounce_minutes: z.number().int().min(1).max(60),
    /** Whether an event or nightly solve may publish itself under §3.5's tests (D-399 ruling 2). */
    schedule_auto_publish: z.boolean(),
    /** How many hours ahead each lane keeps its published sequence; 0 turns the fence off (D-397 ruling 7). */
    schedule_frozen_hours: z.number().int().min(0).max(72),
    /** The business date whose nightly solve the worker last claimed; null means never. Read-only (P3-I-2). */
    nightly_solved_on: PlanningDate.nullable(),
    /** The share of a work centre's labour rate an overtime move is priced at, in per cent (D-402 ruling 2, P3-I-3). */
    overtime_cost_pct: z.number().int().min(100).max(300),
    /** The hour whose hourly promise-watch pass the worker last claimed; null means never. Read-only (P3-J J-3, D-466). */
    promise_watched_at: Timestamp.nullable(),
    /** The business date whose daily verdict sweep the worker last claimed; null means never. Read-only (P3-J J-3, D-467). */
    promise_verdicts_on: PlanningDate.nullable(),
    updated_at: Timestamp.nullable(),
  })
  .openapi("PlanningSettings");

/** `PUT /v1/planning/settings`. Fields added after G-1 join as optional (D-349 ruling 18). */
export const PlanningSettingsPutRequest = z
  .object({
    mps_auto_approve_delta_pct: PlanningPct,
    mps_facility_admin_delta_pct: PlanningPct,
    ctp_safety_margin_days: z.number().int().min(0).max(32767),
    promise_ontime_downgrade_pct: PlanningPct.refine((v) => Number(v) <= 100, {
      message: "must be at most 100",
    }),
    horizon_weeks: z.number().int().min(1).max(52),
    /** Optional so a G-1 client stays valid; an omitted value keeps the stored one (D-349 ruling 18). */
    demand_refresh_debounce_minutes: z.number().int().min(1).max(60).optional(),
    /**
     * The facility calendar's pattern (P3-H-1, D-386 ruling 3). Optional so an
     * older client stays valid; an omitted value keeps the stored one, and null
     * clears the facility's calendar.
     */
    facility_shift_pattern_id: Uuid.nullable().optional(),
    /** P3-H-2 (D-388 ruling 9): optional, an omitted value keeps the stored one. */
    mrp_release_buffer_days: z.number().int().min(0).max(10).optional(),
    mrp_qty_tolerance_pct: PlanningPct.refine((v) => Number(v) <= 100, {
      message: "must be at most 100",
    }).optional(),
    /** P3-I-2 (D-398 ruling 1): optional, an omitted value keeps the stored one. */
    schedule_horizon_days: z.number().int().min(7).max(84).optional(),
    schedule_batching_window_hours: z.number().int().min(1).max(336).optional(),
    schedule_solve_debounce_minutes: z.number().int().min(1).max(60).optional(),
    schedule_auto_publish: z.boolean().optional(),
    schedule_frozen_hours: z.number().int().min(0).max(72).optional(),
    /** P3-I-3 (D-402 ruling 2): optional, an omitted value keeps the stored one. */
    overtime_cost_pct: z.number().int().min(100).max(300).optional(),
    expected_updated_at: ExpectedUpdatedAt,
  })
  .strict()
  .superRefine((body, ctx) => {
    if (Number(body.mps_auto_approve_delta_pct) > Number(body.mps_facility_admin_delta_pct)) {
      for (const field of ["mps_auto_approve_delta_pct", "mps_facility_admin_delta_pct"]) {
        ctx.addIssue({
          code: "custom",
          path: [field],
          message: "the auto-approve bar may not exceed the facility-admin bar",
          params: { reason: "auto_above_facility_admin" },
        });
      }
    }
  })
  .openapi("PlanningSettingsPutRequest");

/**
 * The fields only the group_admin lineage may change: the two MPS bars
 * (D-351 ruling 8). The agent firm cap left these settings for the planner
 * agent's policy row, where the same lineage rule holds (D-526 ruling 2).
 */
export const PlanningSettingsBar = z.enum([
  "mps_auto_approve_delta_pct",
  "mps_facility_admin_delta_pct",
]);

/** 409 `PLANNING_SETTINGS_CHANGED` carries the stored row as `current`. */
export const PlanningSettingsConflictProblem = Problem.extend({
  current: PlanningSettings.optional(),
}).openapi("PlanningSettingsConflictProblem");

/** 403 on the settings PUT. A bar change by a caller outside the group_admin lineage names the bars in `fields`. */
export const PlanningSettingsForbiddenProblem = Problem.extend({
  missing_permission: z.string().optional(),
  fields: z.array(PlanningSettingsBar).optional(),
  required_role_lineage: z.literal("group_admin").optional(),
}).openapi("PlanningSettingsForbiddenProblem");

// --- §2.2 safety-stock policies -----------------------------------------------

export const SafetyStockMethod = z.enum(["static_qty", "days_of_cover"]);
export const PlanningRowStatus = z.enum(["active", "archived"]);
/** The list filter: `active` by default, or `archived`, or `all`. */
export const PlanningStatusFilter = z.enum(["active", "archived", "all"]);

export const SafetyStockPolicy = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    product_id: Uuid,
    method: SafetyStockMethod,
    static_qty: PlanningQty.nullable(),
    days_of_cover: z.number().int().min(1).nullable(),
    valid_from: PlanningDate,
    valid_to: PlanningDate.nullable(),
    status: PlanningRowStatus,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("SafetyStockPolicy");

export const SafetyStockPolicyListQuery = z.object({
  facility_id: Uuid,
  product_id: Uuid.optional(),
  status: PlanningStatusFilter.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const SafetyStockPolicyListResponse = z
  .object({ items: z.array(SafetyStockPolicy), next_cursor: z.string().nullable() })
  .openapi("SafetyStockPolicyListResponse");

/** `PUT /v1/planning/safety-stock-policies/{productId}`; `status: 'archived'` archives the active policy. */
export const SafetyStockPolicyPutRequest = z
  .object({
    method: SafetyStockMethod,
    static_qty: PlanningQty.nullable(),
    days_of_cover: z.number().int().min(1).max(2147483647).nullable(),
    /** Omitted keeps the active policy's date; a new policy starts on the facility's today (spec 03 §2.2). */
    valid_from: PlanningDate.optional(),
    /** Omitted keeps the active policy's end; null clears it (spec 03 §2.2). */
    valid_to: PlanningDate.nullable().optional(),
    status: PlanningRowStatus,
    expected_updated_at: ExpectedUpdatedAt,
  })
  .strict()
  .openapi("SafetyStockPolicyPutRequest");

export const SafetyStockInvalidReason = z.enum([
  "METHOD_FIELDS_MISMATCH",
  "VALID_RANGE_INVALID",
  "NO_PLANNING_PARAMETERS",
]);

/** 422 on the policy PUT: `SAFETY_STOCK_INVALID` names its `reasons`; `PRODUCT_ARCHIVED` names none. */
export const SafetyStockInvalidProblem = Problem.extend({
  reasons: z.array(SafetyStockInvalidReason).optional(),
}).openapi("SafetyStockInvalidProblem");

/** 409 `SAFETY_STOCK_CHANGED` carries the active policy as `current`, null when none is active. */
export const SafetyStockConflictProblem = Problem.extend({
  current: SafetyStockPolicy.nullable().optional(),
}).openapi("SafetyStockConflictProblem");

// --- §2.1 planning parameters -------------------------------------------------

export const ReplenishmentType = z.enum(["make", "buy", "transfer"]);
export const LotSizingMethod = z.enum(["lot_for_lot", "min_multiple"]);

const PlannerCode = z
  .string()
  .min(1)
  .max(64)
  .refine((v) => v === v.trim(), { message: "must not start or end with a space" });

export const PlanningParameters = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    product_id: Uuid,
    replenishment_type: ReplenishmentType,
    source_facility_id: Uuid.nullable(),
    mps_relevant: z.boolean(),
    lead_time_days: z.number().int().min(0),
    lot_sizing_method: LotSizingMethod,
    min_lot_qty: PlanningQty.nullable(),
    lot_multiple_qty: PlanningQty.nullable(),
    frozen_fence_days: z.number().int().min(0),
    reschedule_tolerance_days: z.number().int().min(0),
    planner_code: z.string().nullable(),
    status: PlanningRowStatus,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("PlanningParameters");

/** A list row embeds the product's active safety-stock policy at the facility (spec 03 §2.1). */
export const PlanningParametersListItem = PlanningParameters.extend({
  safety_stock_policy: SafetyStockPolicy.nullable(),
}).openapi("PlanningParametersListItem");

export const PlanningParametersListQuery = z.object({
  facility_id: Uuid,
  product_id: Uuid.optional(),
  replenishment_type: ReplenishmentType.optional(),
  mps_relevant: z.enum(["true", "false"]).optional(),
  planner_code: z.string().min(1).max(64).optional(),
  status: PlanningStatusFilter.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const PlanningParametersListResponse = z
  .object({ items: z.array(PlanningParametersListItem), next_cursor: z.string().nullable() })
  .openapi("PlanningParametersListResponse");

/** `PUT /v1/planning/parameters/{productId}`: the full row (spec 03 §2.1). */
export const PlanningParametersPutRequest = z
  .object({
    replenishment_type: ReplenishmentType,
    source_facility_id: Uuid.nullable(),
    mps_relevant: z.boolean(),
    lead_time_days: z.number().int().min(0).max(2147483647),
    lot_sizing_method: LotSizingMethod,
    min_lot_qty: PlanningPositiveQty.nullable(),
    lot_multiple_qty: PlanningPositiveQty.nullable(),
    frozen_fence_days: z.number().int().min(0).max(2147483647),
    reschedule_tolerance_days: z.number().int().min(0).max(2147483647),
    planner_code: PlannerCode.nullable(),
    status: PlanningRowStatus,
    expected_updated_at: ExpectedUpdatedAt,
  })
  .strict()
  .openapi("PlanningParametersPutRequest");

export const PlanningParametersInvalidReason = z.enum([
  "METHOD_FIELDS_MISMATCH",
  "TRANSFER_SOURCE_REQUIRED",
  "SOURCE_NOT_ALLOWED",
  "SOURCE_FACILITY_INVALID",
]);

/** 422 on the parameters PUT: `PLANNING_PARAMETERS_INVALID` names its `reasons`; `PRODUCT_ARCHIVED` names none. */
export const PlanningParametersInvalidProblem = Problem.extend({
  reasons: z.array(PlanningParametersInvalidReason).optional(),
}).openapi("PlanningParametersInvalidProblem");

/** 409 `PLANNING_PARAMETERS_CHANGED` carries the stored row as `current`. */
export const PlanningParametersConflictProblem = Problem.extend({
  current: PlanningParameters.optional(),
}).openapi("PlanningParametersConflictProblem");

/** The `{productId}` path parameter of the two product-keyed PUTs. */
export const PlanningProductParam = z.object({ productId: Uuid });

// --- §2.3 and §2.4 forecasts --------------------------------------------------

export const ForecastStatus = z.enum(["draft", "active", "archived"]);
export const ForecastConsumptionMode = z.enum(["consume", "additive"]);

const ForecastName = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .openapi({ description: "At most 120 characters after trimming" });

const ForecastWindowDays = z.number().int().min(0).max(91);

export const Forecast = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    name: z.string(),
    status: ForecastStatus,
    consumption_mode: ForecastConsumptionMode,
    consumption_window_days: z.number().int().min(0).max(91),
    /** Every line of the forecast, inside any window or not. */
    line_count: z.number().int().min(0),
    created_by: Uuid,
    activated_at: Timestamp.nullable(),
    archived_at: Timestamp.nullable(),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("Forecast");

export const ForecastListQuery = z.object({
  facility_id: Uuid,
  status: ForecastStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const ForecastListResponse = z
  .object({ items: z.array(Forecast), next_cursor: z.string().nullable() })
  .openapi("ForecastListResponse");

export const ForecastCreateRequest = z
  .object({
    facility_id: Uuid,
    name: ForecastName,
    consumption_mode: ForecastConsumptionMode.optional(),
    consumption_window_days: ForecastWindowDays.optional(),
  })
  .strict()
  .openapi("ForecastCreateRequest");

/** `PATCH /v1/forecasts/{id}`. `status` moves draft to active, and draft or active to archived. */
export const ForecastPatchRequest = z
  .object({
    name: ForecastName.optional(),
    consumption_mode: ForecastConsumptionMode.optional(),
    consumption_window_days: ForecastWindowDays.optional(),
    status: z.enum(["active", "archived"]).optional(),
    expected_updated_at: Timestamp,
  })
  .strict()
  .openapi("ForecastPatchRequest");

/** 409 on a forecast write carries the stored header as `current` (`FORECAST_CHANGED` always). */
export const ForecastConflictProblem = Problem.extend({
  current: Forecast.optional(),
}).openapi("ForecastConflictProblem");

export const ForecastIdParam = z.object({ id: Uuid });

/** `GET /v1/forecasts/{id}`: both bounds inclusive Mondays, at most 104 weeks (spec 03 §5). */
export const ForecastDetailQuery = z.object({
  bucket_from: PlanningMonday.optional(),
  bucket_to: PlanningMonday.optional(),
});

export const ForecastLine = z
  .object({
    id: Uuid,
    product_id: Uuid,
    bucket_start: PlanningDate,
    qty: PlanningQty,
    note: z.string().nullable(),
    updated_at: Timestamp,
  })
  .openapi("ForecastLine");

/** The header, the lines of one window, and how many lines lie outside it. */
export const ForecastDetail = z
  .object({
    forecast: Forecast,
    bucket_from: PlanningDate,
    bucket_to: PlanningDate,
    lines: z.array(ForecastLine),
    lines_outside_window: z.number().int().min(0),
  })
  .openapi("ForecastDetail");

/**
 * One row of a lines PUT. `bucket_start`, `qty` and `note` are judged per row
 * by the service, so a bad value is a row code in the 422 report (spec 03 §5).
 */
export const ForecastLineInput = z
  .object({
    product_id: Uuid,
    bucket_start: PlanningDate,
    qty: z
      .string()
      .regex(/^-?\d+(\.\d+)?$/)
      .openapi({ example: "3000.000" }),
    note: z.string().nullable().optional(),
  })
  .strict()
  .openapi("ForecastLineInput");

/** `PUT /v1/forecasts/{id}/lines`: replace the lines of one window (D-349 ruling 16). */
export const ForecastLinesPutRequest = z
  .object({
    bucket_from: PlanningMonday,
    bucket_to: PlanningMonday,
    /** When given, only these products' lines in the window are deleted or zeroed. */
    product_ids: z.array(Uuid).optional(),
    lines: z.array(ForecastLineInput),
    expected_updated_at: Timestamp,
  })
  .strict()
  .openapi("ForecastLinesPutRequest");

/** The per-row codes of 422 `FORECAST_LINES_INVALID` (spec 03 §5). */
export const ForecastLineRowCode = z.enum([
  "BUCKET_NOT_MONDAY",
  "BUCKET_OUTSIDE_WINDOW",
  "DUPLICATE_LINE",
  "PRODUCT_NOT_FOUND",
  "PRODUCT_ARCHIVED",
  "PRODUCT_OUTSIDE_SCOPE",
  "QTY_NEGATIVE",
  "QTY_PRECISION",
  "QTY_TOO_LARGE",
  "NOTE_TOO_LONG",
]);

/** 422 `FORECAST_LINES_INVALID`: each failing row by its index in `lines`, and nothing written. */
export const ForecastLinesInvalidProblem = Problem.extend({
  rows: z
    .array(z.object({ index: z.number().int().min(0), codes: z.array(ForecastLineRowCode) }))
    .optional(),
}).openapi("ForecastLinesInvalidProblem");

/** The most rows one lines PUT may carry; more answers 400 `FORECAST_LINES_TOO_MANY`. */
export const FORECAST_LINES_MAX = 2000;
/** The widest window a forecast read or lines PUT may name; more answers 400 `FORECAST_WINDOW_TOO_WIDE`. */
export const FORECAST_WINDOW_MAX_WEEKS = 104;
/** The longest note a forecast line may carry; longer is the row code `NOTE_TOO_LONG`. */
export const FORECAST_LINE_NOTE_MAX = 500;

// --- §2.5, §2.6, §2.6b and §2.23 the demand pool (G-2) -------------------------

/** `mrp` is the refresh an MRP run makes before it reads its snapshot (P3-H-2, D-385 ruling 2). */
export const DemandRefreshTrigger = z.enum(["cron", "manual", "event", "mrp"]);
export const DemandRefreshStatus = z.enum(["running", "succeeded", "failed"]);
/** `transfer` is §3.1 step 5's demand from another facility's transfer order (P3-H-2, D-387 ruling 6). */
export const DemandSourceKind = z.enum(["sales_order", "forecast", "safety_stock", "transfer"]);
/** Which date a sales-order entry carries (D-349 rulings 1 and 2). */
export const DemandDateBasis = z.enum(["line_promise", "undated", "past_due"]);

/**
 * How a pool row names its sales order to the reader (D-349 ruling 15). The
 * number, line and customer are served only when the reader's customer door
 * answers the customer, and `order_id` only when the reader may open the
 * order. A label with every field null reads "Sales order".
 */
export const SalesOrderLabel = z
  .object({
    order_id: Uuid.nullable(),
    order_no: z.string().nullable(),
    line_no: z.number().int().min(1).nullable(),
    customer_name: z.string().nullable(),
  })
  .openapi("SalesOrderLabel");

/** A line the refresh left out because its unit does not convert to the product's base (D-349 ruling 5). */
export const DemandSkippedLine = z.object({
  source: z.enum(["order_line", "shipment"]),
  sales_order_line_id: Uuid.nullable(),
  shipment_line_id: Uuid.nullable(),
  reason: z.literal("UOM_NOT_CONVERTIBLE"),
});

/**
 * `demand_refreshes.stats` (spec 03 §2.5). Each list holds at most 200 entries
 * and each count is exact. A running or failed run serves `{}`, so every field
 * is optional on the wire; a succeeded run always carries all of them.
 */
export const DemandRefreshStats = z
  .object({
    order_lines: z.number().int().min(0),
    /** One total per base unit, because a sum across units means nothing. */
    forecast_consumed: z.array(z.object({ uom_id: Uuid, qty: PlanningQty })),
    safety_stock_items: z.number().int().min(0),
    entries_written: z.number().int().min(0),
    beyond_horizon: z.number().int().min(0),
    credit_deferred: z.object({
      count: z.number().int().min(0),
      sales_order_line_ids: z.array(Uuid),
    }),
    skipped_count: z.number().int().min(0),
    skipped: z.array(DemandSkippedLine),
    /** §3.1 step 5's entries, one per transfer order another facility sources here (P3-H-2). */
    transfer_entries: z.number().int().min(0),
  })
  .partial()
  .openapi("DemandRefreshStats", {
    description:
      "Empty while the run is running or after it failed; a succeeded run carries every field.",
  });

export const DemandRefresh = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    trigger: DemandRefreshTrigger,
    horizon_start: PlanningDate,
    horizon_weeks: z.number().int().min(1).max(52),
    status: DemandRefreshStatus,
    stats: DemandRefreshStats,
    error: z.string().nullable(),
    /** Written by the finish; null while running and on a failed run (§2.5). */
    mps_relevant_changed: z.boolean().nullable(),
    started_at: Timestamp,
    finished_at: Timestamp.nullable(),
    /** Set once §2.6's purge removed the run's entries; a read naming it answers 409. */
    entries_purged_at: Timestamp.nullable(),
  })
  .openapi("DemandRefresh");

/** One refresh with the labels of the lines its stats list (spec 03 §5 row 15). */
export const DemandRefreshDetail = DemandRefresh.extend({
  line_labels: z.array(z.object({ sales_order_line_id: Uuid, label: SalesOrderLabel })),
}).openapi("DemandRefreshDetail");

/** §2.23's debounce row as the workbench reads it. */
export const DemandPendingRequest = z
  .object({
    first_requested_at: Timestamp,
    due_at: Timestamp,
    last_trigger: z.string(),
    /** The number of changes queued, order events and master-data writes alike. */
    trigger_count: z.number().int().min(1),
    failed_attempts: z.number().int().min(0),
    next_attempt_at: Timestamp.nullable(),
  })
  .openapi("DemandPendingRequest");

export const DemandRefreshListQuery = z.object({
  facility_id: Uuid,
  status: DemandRefreshStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const DemandRefreshListResponse = z
  .object({
    items: z.array(DemandRefresh),
    next_cursor: z.string().nullable(),
    pending_request: DemandPendingRequest.nullable(),
    /** Failed refreshes since the facility's last success (§3.1). */
    consecutive_failures: z.number().int().min(0),
  })
  .openapi("DemandRefreshListResponse");

export const DemandRefreshIdParam = z.object({ id: Uuid });

/** `POST /v1/demand/refresh`; `horizon_weeks` overrides the facility setting for this run. */
export const DemandRefreshRequest = z
  .object({
    facility_id: Uuid,
    horizon_weeks: z.number().int().min(1).max(52).optional(),
  })
  .strict()
  .openapi("DemandRefreshRequest");

/** 202: the claimed run, or the one already running at the facility. */
export const DemandRefreshAccepted = z
  .object({ demand_refresh_id: Uuid, status: DemandRefreshStatus })
  .openapi("DemandRefreshAccepted");

export const DemandEntry = z
  .object({
    id: Uuid,
    product_id: Uuid,
    source_kind: DemandSourceKind,
    sales_order_line_id: Uuid.nullable(),
    forecast_id: Uuid.nullable(),
    forecast_line_id: Uuid.nullable(),
    safety_stock_policy_id: Uuid.nullable(),
    bucket_start: PlanningDate,
    due_date: PlanningDate,
    /** Set on every sales-order entry and on no other. */
    date_basis: DemandDateBasis.nullable(),
    qty_original: PlanningQty,
    qty_consumed: PlanningQty,
    qty_net: PlanningQty,
    /** The order's label for this reader on a sales-order entry; null on any other. */
    label: SalesOrderLabel.nullable(),
    /**
     * The destination's transfer order a transfer entry projects, served only
     * to a holder of `planning.planned_order.read` and null for every other
     * reader and every other entry (§4.2, D-387 ruling 6).
     */
    transfer_planned_order_id: Uuid.nullable(),
    /**
     * A transfer entry's label: the destination facility always, the order's
     * PLN number only for a holder of `planning.planned_order.read`, and
     * `linkable` when that order lies in the reader's scope. Null on any other
     * entry.
     */
    transfer: z
      .object({
        destination_facility_id: Uuid,
        destination_facility_name: z.string(),
        po_no: z.string().nullable(),
        linkable: z.boolean(),
      })
      .nullable(),
  })
  .openapi("DemandEntry");

/** The net demand of one product in one bucket, by source, over the whole filtered set. */
export const DemandSubtotal = z
  .object({
    product_id: Uuid,
    bucket_start: PlanningDate,
    sales_order: PlanningQty,
    forecast: PlanningQty,
    safety_stock: PlanningQty,
    /** §3.1 step 5's transfer demand (P3-H-2). */
    transfer: PlanningQty,
    total: PlanningQty,
  })
  .openapi("DemandSubtotal");

export const DemandEntriesQuery = z.object({
  facility_id: Uuid,
  /** Default: the facility's latest succeeded refresh. */
  refresh_id: Uuid.optional(),
  product_id: Uuid.optional(),
  source_kind: DemandSourceKind.optional(),
  bucket_from: PlanningMonday.optional(),
  bucket_to: PlanningMonday.optional(),
  planner_code: z.string().min(1).max(64).optional(),
  date_basis: DemandDateBasis.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const DemandEntriesResponse = z
  .object({
    /** Null before the facility's first successful refresh, with no items. */
    refresh: DemandRefresh.nullable(),
    items: z.array(DemandEntry),
    next_cursor: z.string().nullable(),
    subtotals: z.array(DemandSubtotal),
  })
  .openapi("DemandEntriesResponse");

export const ForecastConsumptionQuery = z.object({
  /** Default: the latest succeeded refresh at the forecast's facility. */
  refresh_id: Uuid.optional(),
});

/** One forecast cell of one refresh and the order lines that consumed it (§2.6b). */
export const ForecastConsumptionCell = z
  .object({
    product_id: Uuid,
    bucket_start: PlanningDate,
    forecast_entry_id: Uuid,
    qty_original: PlanningQty,
    qty_consumed: PlanningQty,
    qty_net: PlanningQty,
    consumers: z.array(
      z.object({
        sales_order_line_id: Uuid,
        order_label: SalesOrderLabel,
        qty_consumed: PlanningQty,
      }),
    ),
  })
  .openapi("ForecastConsumptionCell");

export const ForecastConsumption = z
  .object({
    forecast_id: Uuid,
    /** Null when the facility has no succeeded refresh yet, with no cells. */
    demand_refresh_id: Uuid.nullable(),
    cells: z.array(ForecastConsumptionCell),
  })
  .openapi("ForecastConsumption");

// --- §2.7, §2.8 and §3.2 the master production schedule (G-3) -------------------

export const MpsPlanStatus = z.enum([
  "proposed",
  "adjusting",
  "pending_approval",
  "approved",
  "superseded",
]);

/** A projected position, which may stand below 0 (D-350 ruling 2). */
export const PlanningSignedQty = z
  .string()
  .regex(/^-?\d{1,11}(\.\d{1,3})?$/)
  .openapi({
    example: "-200.000",
    description: "Exact decimal, at most 3 places, may be negative",
  });

/**
 * A projection computed on read and never stored, so it is not held to
 * `numeric(14,3)`: an override at the column's limit carries past it.
 */
export const PlanningProjectedQty = z
  .string()
  .regex(/^-?\d+(\.\d{1,3})?$/)
  .openapi({
    example: "-200.000",
    description: "Exact decimal, at most 3 places, may be negative, any width",
  });

/** A sum of `numeric(14,3)` cells, which may exceed one cell's width. */
export const PlanningTotalQty = z
  .string()
  .regex(/^\d+(\.\d{1,3})?$/)
  .openapi({ example: "120000.000", description: "Exact decimal, at most 3 places" });

/** A stored `numeric(7,2)` delta, clamped at 9 999.99 (D-350 ruling 7). */
export const MpsDeltaPct = z
  .string()
  .regex(/^\d{1,4}(\.\d{1,2})?$/)
  .openapi({ example: "12.50", description: "Percent, at most 2 places, at most 9999.99" });

/** One open supply term a bucket counted (§2.8, D-350 ruling 3). */
export const MpsReceiptDetail = z
  .object({
    /** `planned_order` is a firmed planned order at the facility, from P3-H-2 (D-385 ruling 3). */
    kind: z.enum(["work_order", "transfer", "planned_order"]),
    ref_id: Uuid,
    ref_no: z.string(),
    qty: PlanningQty,
    /** Null when the supply carries no date; such supply sits in the first bucket. */
    due: PlanningDate.nullable(),
  })
  .openapi("MpsReceiptDetail");

/**
 * One order a frozen cell's `released_qty` counts: a firmed planned order or
 * a manual work order released against the approved plan, by its number
 * (§2.8, §4.3, D-395 ruling 72).
 */
export const MpsReleasedDetail = z
  .object({
    kind: z.enum(["planned_order", "work_order"]),
    ref_id: Uuid,
    ref_no: z.string(),
    qty: PlanningQty,
    /** The date the order covers the cell on: its due date, or the week's Monday when undated. */
    due: PlanningDate,
  })
  .openapi("MpsReleasedDetail");

/** One base unit's delta against the previous approved plan (§3.2). */
export const MpsUomDelta = z
  .object({ uom_id: Uuid, delta_pct: MpsDeltaPct })
  .openapi("MpsUomDelta");

/** One base unit's sum of the plan's effective quantities (§3.2). */
export const MpsUomTotal = z.object({ uom_id: Uuid, qty: PlanningTotalQty }).openapi("MpsUomTotal");

/** A plan as the list serves it (spec 03 §5 row 18). */
export const MpsPlanSummary = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    plan_no: z.string(),
    status: MpsPlanStatus,
    /** The refresh the plan was proposed from. */
    demand_refresh_id: Uuid,
    horizon_start: PlanningMonday,
    horizon_weeks: z.number().int().min(1).max(52),
    proposed_at: Timestamp,
    submitted_at: Timestamp.nullable(),
    approved_at: Timestamp.nullable(),
    /** True when the plan approved itself on submit (§2.7). */
    auto_approved: z.boolean(),
    /** Null before the first submit and after a reject. */
    delta_pct: MpsDeltaPct.nullable(),
    previous_approved_plan_id: Uuid.nullable(),
    superseded_by_plan_id: Uuid.nullable(),
    rejected_at: Timestamp.nullable(),
    /** True when a newer successful refresh changed MPS-relevant demand after this plan was proposed (§3.2). */
    demand_changed_since_proposal: z.boolean(),
  })
  .openapi("MpsPlanSummary");

/** A plan's header as the plan GET and every MPS mutation serve it (spec 03 §5 row 19). */
export const MpsPlan = MpsPlanSummary.extend({
  submitted_by: Uuid.nullable(),
  approved_by: Uuid.nullable(),
  approval_note: z.string().nullable(),
  rejected_by: Uuid.nullable(),
  rejection_note: z.string().nullable(),
  /** Stored with `delta_pct` at submit and cleared with it (§2.7). */
  delta_by_uom: z.array(MpsUomDelta).nullable(),
  /** Read for this plan alone, over every product, one total per base unit. */
  effective_totals: z.array(MpsUomTotal),
  /** Cells whose demand the latest refresh changed; an integer while `pending_approval`, else null (§3.2). */
  demand_changed_cells: z.number().int().min(0).nullable(),
  notes: z.string().nullable(),
  updated_at: Timestamp,
}).openapi("MpsPlan");

/** One product of a plan page: its base unit and planner code, for the board's rows. */
export const MpsPlanProduct = z
  .object({
    product_id: Uuid,
    /** The product's base unit in module 01; null when the product no longer resolves. */
    base_uom_id: Uuid.nullable(),
    /** The product's planner code at the facility now, null when none. */
    planner_code: z.string().nullable(),
  })
  .openapi("MpsPlanProduct");

/** One cell of a plan (spec 03 §2.8). Every quantity is in the product's base unit. */
export const MpsLine = z
  .object({
    id: Uuid,
    product_id: Uuid,
    bucket_start: PlanningMonday,
    demand_qty: PlanningQty,
    scheduled_receipts_qty: PlanningQty,
    receipt_detail: z.array(MpsReceiptDetail),
    /** The proposal-time snapshot, never rewritten. */
    projected_on_hand_start: PlanningSignedQty,
    /** Recomputed from the effective quantities on every read and never stored (D-350 ruling 4). */
    projected_on_hand_effective: PlanningProjectedQty,
    proposed_qty: PlanningQty,
    override_qty: PlanningQty.nullable(),
    override_reason: z.string().nullable(),
    override_by: Uuid.nullable(),
    override_at: Timestamp.nullable(),
    /** `override_qty` when set, else `proposed_qty`. */
    effective_qty: PlanningQty,
    override_dropped: z.boolean(),
    dropped_override_qty: PlanningQty.nullable(),
    dropped_override_reason: z.string().nullable(),
    dropped_from_plan_id: Uuid.nullable(),
    /** Why the override was dropped: the proposal under it changed, or the cell is now frozen. */
    dropped_cause: z.enum(["proposal_changed", "frozen"]).nullable(),
    is_frozen: z.boolean(),
    /**
     * The part of a frozen cell's quantity that work orders released against
     * the approved plan already carry; the projection counts it once, and it
     * is 0 on every other cell (D-382 ruling 8).
     */
    released_qty: PlanningQty,
    /** The orders `released_qty` counts, named; empty on every other cell (D-395 ruling 72). */
    released_detail: z.array(MpsReleasedDetail),
    updated_at: Timestamp,
  })
  .openapi("MpsLine");

export const MpsPlanListQuery = z.object({
  facility_id: Uuid,
  status: MpsPlanStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const MpsPlanListResponse = z
  .object({ items: z.array(MpsPlanSummary), next_cursor: z.string().nullable() })
  .openapi("MpsPlanListResponse");

export const MpsPlanIdParam = z.object({ id: Uuid });
export const MpsLineParams = z.object({ id: Uuid, lineId: Uuid });

/** The products of one plan page (§5 row 19). */
export const MPS_PLAN_PAGE_PRODUCTS = 50;

export const MpsPlanDetailQuery = z.object({
  /** The product cursor the previous page answered. */
  cursor: PageCursor,
});

/** Another facility that sells this plan's products, sourcing them here by transfer. */
export const MpsTransferDestination = z
  .object({ facility_id: Uuid, name: z.string() })
  .openapi("MpsTransferDestination");

/** One page of a plan: its header, then the cells of up to 50 products. */
export const MpsPlanDetail = z
  .object({
    plan: MpsPlan,
    products: z.array(MpsPlanProduct),
    lines: z.array(MpsLine),
    next_cursor: z.string().nullable(),
    /**
     * The active facilities whose active `transfer` parameters name this
     * plan's facility as the source of one of its products, read across the
     * tenant whatever the caller's scope, ordered by name (D-349 ruling 4).
     */
    transfer_destinations: z.array(MpsTransferDestination),
  })
  .openapi("MpsPlanDetail");

/** `POST /v1/mps/plans/propose` (D-350 ruling 9). */
export const MpsProposeRequest = z
  .object({ facility_id: Uuid })
  .strict()
  .openapi("MpsProposeRequest");

/** The longest override reason, approve note and reject note (§5's length caps). */
export const MPS_NOTE_MAX = 500;

/** `POST /v1/mps/plans/{id}/lines/{lineId}/override` (§5, D-350 ruling 9). */
export const MpsOverrideRequest = z
  .object({
    /** Null clears; a value equal to the proposal clears too. */
    override_qty: PlanningQty.nullable(),
    /** Required when the quantity differs from the proposal. */
    override_reason: z.string().max(MPS_NOTE_MAX).nullable().optional(),
    /** The line's `updated_at`, required; a stale one answers 409 `MPS_LINE_CHANGED`. */
    expected_updated_at: Timestamp,
  })
  .strict()
  .openapi("MpsOverrideRequest");

export const MpsOverrideResponse = z
  .object({ plan: MpsPlan, line: MpsLine })
  .openapi("MpsOverrideResponse");

export const MpsSubmitRequest = z.object({}).strict().openapi("MpsSubmitRequest");

export const MpsApproveRequest = z
  .object({ note: z.string().min(1).max(MPS_NOTE_MAX).optional() })
  .strict()
  .openapi("MpsApproveRequest");

export const MpsRejectRequest = z
  .object({ note: z.string().trim().min(1).max(MPS_NOTE_MAX) })
  .strict()
  .openapi("MpsRejectRequest");

/** 409 `MPS_PLAN_NOT_EDITABLE` and `MPS_PLAN_NOT_PENDING` carry the status the plan reads (D-383 ruling 2). */
export const MpsPlanStatusProblem = Problem.extend({
  plan_status: MpsPlanStatus.optional(),
}).openapi("MpsPlanStatusProblem");

/** 409 on propose: `MPS_PLAN_PENDING_APPROVAL` names the plan that waits (D-383 ruling 2). */
export const MpsProposeConflictProblem = Problem.extend({
  mps_plan_id: Uuid.optional(),
}).openapi("MpsProposeConflictProblem");

/**
 * 409 on an override: `MPS_LINE_CHANGED` carries the line as it stands, and
 * `MPS_PLAN_NOT_EDITABLE` the status the plan reads.
 */
export const MpsLineConflictProblem = Problem.extend({
  current: MpsLine.optional(),
  plan_status: MpsPlanStatus.optional(),
}).openapi("MpsLineConflictProblem");

/** 403 on approve: a missing code, or a delta above the caller's lineage bar (§3.2). */
export const MpsApproveForbiddenProblem = Problem.extend({
  missing_permission: z.string().optional(),
  delta_pct: MpsDeltaPct.optional(),
  required_role_lineage: z.literal("group_admin").optional(),
}).openapi("MpsApproveForbiddenProblem");

// --- audit actions (spec 03 §5) -------------------------------------------------

export const PLANNING_AUDIT_ACTIONS = {
  settingsUpdated: "planning_settings.updated",
  parametersUpserted: "planning_parameters.upserted",
  parametersArchived: "planning_parameters.archived",
  safetyStockUpserted: "safety_stock_policy.upserted",
  safetyStockArchived: "safety_stock_policy.archived",
  forecastCreated: "forecast.created",
  forecastUpdated: "forecast.updated",
  forecastActivated: "forecast.activated",
  forecastArchived: "forecast.archived",
  forecastLinesReplaced: "forecast.lines_replaced",
  demandRefreshRequested: "demand_refresh.requested",
  mpsOverridden: "mps_plan.overridden",
  mpsSubmitted: "mps_plan.submitted",
  mpsApproved: "mps_plan.approved",
  mpsRejected: "mps_plan.rejected",
  mpsProposedManually: "mps_plan.proposed_manually",
} as const;

// --- problem codes ----------------------------------------------------------------

/** Every code module 03 raises. Later PRs add theirs in the commit that raises them. */
export const planningProblemCodes = [
  "FACILITY_NOT_FOUND",
  "PRODUCT_NOT_FOUND",
  "PRODUCT_ARCHIVED",
  "PLANNING_SETTINGS_CHANGED",
  "PLANNING_PARAMETERS_NOT_FOUND",
  "PLANNING_PARAMETERS_INVALID",
  "PLANNING_PARAMETERS_CHANGED",
  "SAFETY_STOCK_NOT_FOUND",
  "SAFETY_STOCK_INVALID",
  "SAFETY_STOCK_CHANGED",
  "FORECAST_NOT_FOUND",
  "FORECAST_NAME_TAKEN",
  "FORECAST_CHANGED",
  "FORECAST_ARCHIVED",
  "FORECAST_STATUS_TRANSITION_INVALID",
  "FORECAST_WINDOW_TOO_WIDE",
  "FORECAST_LINES_TOO_MANY",
  "FORECAST_LINES_INVALID",
  "DEMAND_REFRESH_NOT_FOUND",
  "DEMAND_REFRESH_NOT_SUCCEEDED",
  "DEMAND_REFRESH_PURGED",
  // G-3, the MPS (spec 03 §5 rows 18 to 24).
  "MPS_PLAN_NOT_FOUND",
  "MPS_LINE_NOT_FOUND",
  "MPS_PLAN_NOT_EDITABLE",
  "MPS_LINE_FROZEN",
  "MPS_OVERRIDE_REASON_REQUIRED",
  "MPS_LINE_CHANGED",
  "MPS_PLAN_NOT_PENDING",
  "MPS_PLAN_PENDING_APPROVAL",
  "MPS_NO_RELEVANT_PRODUCTS",
  "DEMAND_REFRESH_MISSING",
  "APPROVAL_THRESHOLD_EXCEEDED",
  // P3-H-1, the calendars (spec 03 §5 rows 26 to 37).
  "SHIFT_PATTERN_NOT_FOUND",
  "SHIFT_PATTERN_INVALID",
  "SHIFT_PATTERN_CODE_TAKEN",
  "SHIFT_PATTERN_CHANGED",
  "SHIFT_PATTERN_OUT_OF_SCOPE",
  "SHIFT_PATTERN_IN_USE",
  "WORK_CENTER_NOT_FOUND",
  "WORK_CENTER_CALENDAR_NOT_FOUND",
  "OVERLAPPING_ASSIGNMENT",
  "ASSIGNMENT_IN_EFFECT",
  "CALENDAR_EXCEPTION_NOT_FOUND",
  "CALENDAR_EXCEPTION_INVALID",
  // P3-H-2, MRP (spec 03 §5 rows 38 to 49).
  "MRP_RUN_NOT_FOUND",
  "MRP_GROUP_SCOPE_REQUIRED",
  "MRP_GROUP_PASS_RUNNING",
  "PLANNED_ORDER_NOT_FOUND",
  "PLANNED_ORDER_NOT_PROPOSED",
  "PLANNED_ORDER_NOT_FIRMED",
  "NO_ACTION_MESSAGE",
  "ACTION_MESSAGE_CHANGED",
  "ACTION_MESSAGE_NOT_BULK_ACCEPTABLE",
  // P3-H-3, conversion (spec 03 §3.4, §5 row 50, D-389 rulings 4 and 6).
  "PURCHASE_CONVERSION_UNAVAILABLE",
  "PLANNED_ORDER_NO_ROUTING",
  "PLANNED_ORDER_PIN_INVALID",
  // Row 50's one success code: an order converted before answers its existing
  // reference, as success, under this code (D-389 ruling 3).
  "ALREADY_CONVERTED",
  // P3-I-2, the setup matrix and the schedule (spec 03 §5 rows 54 to 66).
  "SETUP_MATRIX_CHANGED",
  "SETUP_MATRIX_INVALID",
  "SCHEDULE_RUN_NOT_FOUND",
  "SCHEDULE_NOT_FOUND",
  "SCHEDULE_NOT_DRAFT",
  "SCHEDULE_SUPERSEDED",
  // P3-I-3, the moves, the drag and the directives (spec 03 §5 rows 67 to 71,
  // D-402, D-403, D-413); OP_NOT_MOVABLE decided in P3-I-3's build (D-415 ruling 5).
  "SCHEDULE_OVERLOAD_NOT_FOUND",
  "MOVE_ALREADY_APPLIED",
  "MOVE_NOT_FOUND",
  "MOVE_NOT_APPLICABLE",
  "SCHEDULE_OPERATION_NOT_FOUND",
  "OP_IN_PROGRESS",
  "OP_NOT_MOVABLE",
  "MOVE_IN_PAST",
  "WORK_CENTER_NOT_ALLOWED",
  "PIN_OVERLAPS_FIXED_WORK",
  "PREVIEW_STALE",
  "MOVE_BREAKS_PRECEDENCE",
  "SCHEDULE_DIRECTIVE_NOT_FOUND",
  "SCHEDULE_DIRECTIVE_NOT_ACTIVE",
  // P3-J J-1, promises (spec 03 §5 rows 74 to 78, D-461 to D-464).
  "ORDER_LINE_NOT_FOUND",
  "CAPACITY_PATH_UNAVAILABLE",
  "CTP_BUSY",
  "PROMISE_NOT_FOUND",
  "PROMISE_NOT_QUOTED",
  "PROMISE_EXPIRED",
  "ORDER_LINE_REQUIRED",
  "ORDER_LINE_NOT_PROMISABLE",
  "LINE_ALREADY_PROMISED",
  "BASIS_STALE",
  "PROMISE_NOT_WITHDRAWABLE",
  // P3-J J-4, the what-if sandbox (spec 03 §5 rows 80 to 85, D-471 to D-473).
  "SCENARIO_NOT_FOUND",
  "SCENARIO_LIMIT_REACHED",
  "SCENARIO_NOT_SOLVABLE",
  "SCENARIO_NOT_SOLVED",
  "SCENARIO_CLOSED",
  "NO_PUBLISHED_SCHEDULE",
  "NOT_PERSISTABLE",
] as const;

export type PlanningProblemCode = (typeof planningProblemCodes)[number];
